import type { ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { cn } from "@headerhawk/ui";
import { navEntry, type NavSectionKey } from "@/lib/navigation";
import { useSiteScope, withSiteScope } from "@/lib/hooks/use-site-scope";

interface SectionLayoutProps {
  /** The registry key of the section this draws (ADR 013). */
  section: NavSectionKey;
  /**
   * Something to draw beside a tab's label, keyed by the tab's `href`: a
   * count a section wants seen from any of its tabs, such as Headers ›
   * Changes' "2 this week" (task 374).
   */
  tabBadges?: Readonly<Record<string, ReactNode>>;
}

/**
 * A section's heading and tab row (ADR 013).
 *
 * Each tab is a route, so it has a URL of its own and the sidebar highlights
 * the section on any of them. The row is drawn from the navigation registry,
 * which is also what `route-integrity.test.ts` checks against `App.tsx`.
 */
export function SectionLayout({ section, tabBadges }: SectionLayoutProps) {
  const entry = navEntry(section);
  const { siteId } = useSiteScope();
  const tabs = entry.tabs ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-3 border-b border-border px-4 pt-4 lg:px-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {entry.label}
          </h1>
        </div>
        {tabs.length > 0 && (
          <nav className="flex gap-1" aria-label={`${entry.label} views`}>
            {tabs.map((tab) => (
              // `end` on the section's own href: every other tab sits under
              // it, so a prefix match would light both.
              <NavLink
                key={tab.href}
                to={withSiteScope(tab.href, siteId)}
                end={tab.href === entry.href}
                className={({ isActive }) =>
                  cn(
                    "-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "border-foreground text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )
                }
              >
                {tab.label}
                {tabBadges?.[tab.href]}
              </NavLink>
            ))}
          </nav>
        )}
      </div>
      <Outlet />
    </div>
  );
}
